import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spacer,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import React from 'react'

interface ConfirmModalProps {
  isOpen: boolean
  isLoading?: boolean

  title: string
  description: string
  icon: React.ReactNode
  onClose: () => void
  onConfirm?: () => void
  isConfirmDisabled?: boolean
  onSecondaryAction?: () => void
  secondaryActionText?: string
  children?: React.ReactNode
  hideChildrenSpacer?: boolean
  confirmButtonText?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  finalFocusRef?: React.RefObject<any>
  isSecondaryLoading?: boolean
  showCloseAndContinue?: boolean // Show "Close & Continue in Background" button
  closeOnEsc?: boolean
  closeOnOverlayClick?: boolean
  hideCloseButton?: boolean
  preserveScroll?: boolean
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isConfirmDisabled,
  isLoading,
  icon,
  title,
  confirmButtonText,
  onSecondaryAction,
  secondaryActionText,
  isSecondaryLoading,
  children,
  hideChildrenSpacer,
  description,
  finalFocusRef,
  showCloseAndContinue,
  closeOnEsc = true,
  closeOnOverlayClick = true,
  hideCloseButton = false,
  preserveScroll = false,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false

  // A lone confirm button reads best full-width; a Cancel + Confirm pair sits
  // right-aligned on desktop (stacked on mobile). Both share the button styling.
  const hasSecondary = !!onSecondaryAction || (isLoading && showCloseAndContinue)
  const fullWidthConfirm = isMobile || !hasSecondary

  const renderActions = () =>
    onConfirm && (
      <Flex
        gap="12px"
        direction={isMobile ? 'column-reverse' : 'row'}
        justify={isMobile || !hasSecondary ? 'stretch' : 'flex-end'}
        px={isMobile ? '20px' : '32px'}
        pt="2px"
        pb={isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 20px)' : '26px'}
      >
        {isLoading && showCloseAndContinue ? (
          <Button
            variant="outline"
            w="100%"
            h="48px"
            fontSize={isMobile ? '15px' : '14px'}
            fontWeight="600"
            borderRadius={isMobile ? '14px' : '10px'}
            sx={{ WebkitTapHighlightColor: 'transparent' }}
            _active={{ transform: 'scale(0.98)' }}
            onClick={onClose}
            colorScheme="primary"
          >
            Close & Continue in Background
          </Button>
        ) : (
          <>
            {onSecondaryAction && (
              <Button
                variant="default"
                w={isMobile ? '100%' : 'auto'}
                h={isMobile ? '48px' : '42px'}
                fontSize={isMobile ? '15px' : '14px'}
                fontWeight="600"
                px={isMobile ? '12px' : '22px'}
                borderRadius={isMobile ? '14px' : '10px'}
                sx={{ WebkitTapHighlightColor: 'transparent' }}
                _active={{ transform: 'scale(0.98)' }}
                onClick={onSecondaryAction}
                isLoading={isSecondaryLoading}
              >
                {secondaryActionText ?? 'Cancel'}
              </Button>
            )}
            <Button
              variant="heavy"
              w={fullWidthConfirm ? '100%' : 'auto'}
              h={isMobile ? '50px' : '42px'}
              fontSize={isMobile ? '15px' : '14px'}
              fontWeight={isMobile ? '600' : '700'}
              px={isMobile ? '12px' : '24px'}
              borderRadius={isMobile ? '14px' : '10px'}
              boxShadow={isConfirmDisabled ? 'none' : '0 4px 14px rgba(99, 102, 241, 0.25)'}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={onConfirm}
              isLoading={isLoading}
              isDisabled={isConfirmDisabled}
            >
              {confirmButtonText ?? 'Continue'}
            </Button>
          </>
        )}
      </Flex>
    )

  if (isMobile) {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom"
        autoFocus={false}
        closeOnEsc={closeOnEsc}
        closeOnOverlayClick={closeOnOverlayClick}
        blockScrollOnMount={!preserveScroll}
        returnFocusOnClose={!preserveScroll}
        finalFocusRef={finalFocusRef}
      >
        <DrawerOverlay bg="rgba(10, 10, 20, 0.55)" sx={{ backdropFilter: 'blur(6px)' }} />
        <DrawerContent
          bg="white"
          borderTopRadius="24px"
          maxH="90dvh"
          boxShadow="0px -8px 32px -8px rgba(15, 23, 42, 0.18)"
          sx={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Drag handle */}
          <Center pt="10px" pb="2px" flexShrink={0}>
            <Box w="40px" h="5px" borderRadius="full" bg="neutral.200" />
          </Center>

          <Box flex="1" overflowY="auto" px="24px" pt="12px" pb="4px">
            <Center>
              <Center
                w="52px"
                h="52px"
                borderRadius="14px"
                background="linear-gradient(135deg, #818CF8, #6366F1)"
                boxShadow="0 4px 12px rgba(99, 102, 241, 0.28)"
              >
                {icon}
              </Center>
            </Center>
            <Text
              mt="16px"
              fontSize="18px"
              fontWeight="700"
              lineHeight="1.3"
              letterSpacing="-0.01em"
              textAlign="center"
              color="neutral.900"
            >
              {title}
            </Text>
            <Text
              mt="6px"
              fontSize="13px"
              lineHeight="1.5"
              textAlign="center"
              color="neutral.500"
              fontWeight="400"
            >
              {description}
            </Text>
            {children && !hideChildrenSpacer && <Spacer h="18px" />}
            {children}
          </Box>

          {renderActions()}
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
      finalFocusRef={finalFocusRef}
      closeOnEsc={closeOnEsc}
      closeOnOverlayClick={closeOnOverlayClick}
      blockScrollOnMount={!preserveScroll}
      returnFocusOnClose={!preserveScroll}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        mx="auto"
        my="auto"
        p="0"
        w="464px"
        maxW="464px"
        maxH="calc(100vh - 120px)"
        borderRadius="18px"
        overflow="hidden"
        boxShadow="0 24px 60px -12px rgba(15, 23, 42, 0.28)"
        sx={{ padding: '0 !important' }}
      >
        {!hideCloseButton && (
          <ModalCloseButton
            color="neutral.500"
            _hover={{ bg: 'neutral.50' }}
            top="16px"
            right="16px"
            borderRadius="full"
          />
        )}
        <ModalBody px="32px" pt="34px" pb="22px">
          <Center>
            <Center
              w="56px"
              h="56px"
              borderRadius="16px"
              background="linear-gradient(135deg, #818CF8, #6366F1)"
              boxShadow="0 4px 12px rgba(99, 102, 241, 0.28)"
            >
              {icon}
            </Center>
          </Center>
          <Text
            mt="18px"
            fontSize="19px"
            fontWeight="700"
            lineHeight="1.3"
            letterSpacing="-0.01em"
            textAlign="center"
            color="neutral.900"
          >
            {title}
          </Text>
          <Text
            mt="8px"
            fontSize="14px"
            lineHeight="1.55"
            textAlign="center"
            color="neutral.500"
            fontWeight="400"
          >
            {description}
          </Text>
          {children && !hideChildrenSpacer && <Spacer h="22px" />}
          {children}
        </ModalBody>
        {renderActions()}
      </ModalContent>
    </Modal>
  )
}

export default ConfirmModal
